import { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { Phone, Plus, ChevronDown, RefreshCw, Copy, Check, Globe, X, Zap, TrendingUp, Server, AlertTriangle, Info, Shield, Repeat } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Provider card data for US Numbers - 4 providers (names hidden from users)
const SERVER_OPTIONS = [
  { value: 'us_server',       label: 'US Server',       desc: 'Non-VoIP US numbers · Highest success rate',   icon: Shield, badge: null },
  { value: 'server1',         label: 'International',   desc: 'Worldwide coverage · Price varies by country', icon: Globe,  badge: null },
  { value: 'server2',         label: 'Global',          desc: 'Wide service range · Reliable delivery',       icon: Repeat, badge: null },
  { value: 'us_numbers',      label: 'US Numbers',      desc: 'Choose your provider · Premium routes',        icon: Zap,    badge: 'NEW' },
  { value: 'other_countries', label: 'Other Countries', desc: 'Provider choice · 100+ countries',             icon: Server, badge: 'NEW' },
];

const US_PROVIDERS = [
  {
    id: 'textverified',
    name: 'Premium Server',
    description: 'High success rate US numbers',
    icon: '🔐',
    color: 'from-primary to-primary',
    badge: 'Premium',
    features: ['High Success Rate', 'Fast Delivery']
  },
  {
    id: '5sim',
    name: 'Server 1',
    description: 'Reliable US number support',
    icon: '🌐',
    color: 'from-primary to-primary',
    badge: 'Popular',
    features: ['Wide Service Range', 'Reliable']
  },
  {
    id: 'smsbower',
    name: 'Budget Server',
    description: 'Affordable US numbers',
    icon: '📱',
    color: 'from-green-500 to-green-600',
    badge: 'Budget',
    features: ['Low Prices', 'Good Availability']
  },
  {
    id: 'tigersms',
    name: 'Fast Server',
    description: 'Quick US verifications',
    icon: '🐯',
    color: 'from-orange-500 to-orange-600',
    badge: 'Fast',
    features: ['Quick Delivery', 'Many Services']
  }
];

// Provider card data for Other Countries - 3 providers (names hidden from users)
const OTHER_COUNTRIES_PROVIDERS = [
  {
    id: '5sim',
    name: 'Server 1',
    description: 'Extensive global coverage',
    icon: '🌐',
    color: 'from-primary to-primary',
    badge: 'Reliable',
    features: ['Worldwide', 'Many Services']
  },
  {
    id: 'smsbower',
    name: 'Budget Server',
    description: 'Affordable international numbers',
    icon: '📱',
    color: 'from-green-500 to-green-600',
    badge: 'Budget',
    features: ['Low Prices', 'Good Coverage']
  },
  {
    id: 'tigersms',
    name: 'Fast Server',
    description: 'Quick global verifications',
    icon: '🐯',
    color: 'from-orange-500 to-orange-600',
    badge: 'Fast',
    features: ['Quick Delivery', 'Many Countries']
  }
];

// Country flag helper
const getCountryFlagUrl = (countryValue) => {
  if (!countryValue) return null;
  const key = String(countryValue).toLowerCase().replace(/\s+/g, '');
  
  const known = {
    usa: 'us', unitedstates: 'us', nigeria: 'ng', unitedkingdom: 'gb',
    uk: 'gb', canada: 'ca', india: 'in', germany: 'de', france: 'fr',
    spain: 'es', italy: 'it', brazil: 'br', russia: 'ru', china: 'cn',
    japan: 'jp', australia: 'au', netherlands: 'nl', sweden: 'se',
    indonesia: 'id', philippines: 'ph', vietnam: 'vn', thailand: 'th',
    malaysia: 'my', singapore: 'sg', southkorea: 'kr', mexico: 'mx',
    argentina: 'ar', colombia: 'co', chile: 'cl', peru: 'pe',
    poland: 'pl', ukraine: 'ua', romania: 'ro', turkey: 'tr',
    southafrica: 'za', egypt: 'eg', kenya: 'ke', ghana: 'gh'
  };

  const iso2 = known[key] || (/^[a-z]{2}$/.test(key) ? key : null);
  if (!iso2) return null;
  return `https://flagcdn.com/${iso2}.svg`;
};

// Minimum price enforcement - ₦500 for all services
const MIN_PRICE_NGN = 500;
const enforceMinPrice = (priceNgn) => {
  if (!priceNgn || priceNgn <= 0) return 0;
  return Math.max(MIN_PRICE_NGN, priceNgn);
};


// Shared Select styles to prevent blurry text and ensure dark, visible text
const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '48px',
    borderWidth: '2px',
    borderColor: 'hsl(var(--input))',
    borderRadius: 12,
    '&:hover': { borderColor: 'hsl(var(--primary))' }
  }),
  placeholder: (base) => ({
    ...base,
    color: 'hsl(var(--muted-foreground))',
    fontSize: '0.8rem',
    fontWeight: 500
  }),
  singleValue: (base) => ({
    ...base,
    color: 'hsl(var(--foreground))',
    fontWeight: 600,
    fontSize: '0.85rem'
  }),
  input: (base) => ({
    ...base,
    color: 'hsl(var(--foreground))'
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'hsl(var(--foreground) / .07)' : state.isSelected ? 'hsl(var(--primary) / 0.12)' : 'hsl(var(--card))',
    color: 'hsl(var(--foreground))',
    cursor: 'pointer',
    fontWeight: state.isSelected ? 700 : 500,
    fontSize: '0.8rem',
    borderBottom: '1px solid #f3f4f6',
    borderRadius: 0
  })
};

// Virtual Numbers (DaisySMS + SMS-pool + Tiger placeholder)
// Extracted into its own component to prevent remounts that caused dropdown
// menus to close while users were typing.
export function VirtualNumbersSection({ user, orders, axiosConfig, fetchOrders, fetchProfile }) {
  const [, setTick] = useState(0);

  // Virtual Numbers state (local to this component)
  const [selectedServer, setSelectedServer] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [serviceMenuOpen, setServiceMenuOpen] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [selectedPool, setSelectedPool] = useState(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);

  // NEW: Provider cards state for US Numbers and Other Countries
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerServices, setProviderServices] = useState([]);
  const [providerCountries, setProviderCountries] = useState([]);
  const [providerServicesLoading, setProviderServicesLoading] = useState(false);
  const [providersStatus, setProvidersStatus] = useState({});

  // NEW: Provider/Operator selection modal state
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [selectedServiceProvider, setSelectedServiceProvider] = useState(null);
  const [providersLoading, setProvidersLoading] = useState(false);

  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [selectedAreaCodes, setSelectedAreaCodes] = useState([]);
  const [preferredNumber, setPreferredNumber] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [purchaseExpanded, setPurchaseExpanded] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  // State for collapsible tips sections
  const [issuesTipOpen, setIssuesTipOpen] = useState(false);
  const [buyTipsOpen, setBuyTipsOpen] = useState(false);

  // Service code to full name mapping (comprehensive)
  const serviceNames = {
    // Social Media
    wa: 'WhatsApp', tg: 'Telegram', fb: 'Facebook', ig: 'Instagram',
    tw: 'Twitter', vk: 'VKontakte', ok: 'Odnoklassniki', vi: 'Viber',
    we: 'WeChat', li: 'LinkedIn', sn: 'Snapchat', tt: 'TikTok',
    dc: 'Discord', ds: 'Discord', sg: 'Signal', th: 'Threads', rd: 'Reddit',
    pi: 'Pinterest', tm: 'Tumblr', sk: 'Skype', ln: 'Line',
    kk: 'KakaoTalk', zl: 'Zalo',
    
    // Dating
    ti: 'Tinder', bm: 'Bumble', bd: 'Badoo', hg: 'Hinge',
    hw: 'Happn', mf: 'Match', gr: 'Grindr',
    
    // E-commerce & Delivery
    am: 'Amazon', eb: 'eBay', al: 'AliExpress', wb: 'Wildberries',
    oz: 'Ozon', av: 'Avito', ub: 'Uber', ly: 'Lyft', dd: 'DoorDash',
    ue: 'UberEats', ic: 'Instacart', sh: 'Shein',
    
    // Finance
    pp: 'PayPal', ca: 'Cash App', vn: 'Venmo', za: 'Zelle',
    cb: 'Coinbase', ba: 'Binance', kr: 'Kraken', rv: 'Revolut',
    ws: 'Wise', ro: 'Robinhood', st: 'Stripe', sq: 'Square',
    
    // Tech & Services
    go: 'Google', gl: 'Gmail', ms: 'Microsoft', ap: 'Apple',
    ya: 'Yahoo', dr: 'Dropbox', zo: 'Zoom', sl: 'Slack',
    gh: 'GitHub', sp: 'Spotify', nf: 'Netflix', oa: 'OpenAI/ChatGPT',
    yt: 'YouTube', ai: 'Airbnb', bk: 'Booking',
    
    // Gaming
    ps: 'PlayStation', xb: 'Xbox', ni: 'Nintendo', mc: 'Minecraft',
    fo: 'Fortnite', ep: 'Epic Games',
    
    // Other common
    dh: 'DoorDash', sb: 'Starbucks', an: 'Any Service',
    ot: 'Other', nw: 'New Service', mm: 'Miscellaneous'
  };

  const getServiceName = (code) => {
    if (!code) return '';
    const lowerCode = code.toLowerCase();
    return serviceNames[lowerCode] || code.toUpperCase();
  };

  // Get timer duration based on provider (in seconds)
  // Timer durations based on server name
  const getTimerDuration = (order) => {
    const serverName = (order.server_name || '').toLowerCase();
    if (serverName.includes('server 1')) return 20 * 60; // 20 minutes (5sim)
    if (serverName.includes('budget')) return 25 * 60; // 25 minutes (smsbower)
    if (serverName.includes('premium')) return 5 * 60; // 5 minutes (textverified)
    return 10 * 60; // Default 10 minutes
  };

  // Format phone number for DISPLAY: clean up malformed data, add + prefix (except Premium Server shows +1 prefix)
  const formatPhoneDisplay = (phone, serverName) => {
    if (!phone) return 'N/A';
    let cleaned = String(phone).replace(/['"]/g, '').trim();
    if (cleaned.includes(',') || cleaned.includes('phone_number')) {
      const parts = cleaned.split(',');
      cleaned = parts[0].trim().replace(/[^0-9+]/g, '');
    }
    cleaned = cleaned.replace(/[^0-9+]/g, '');
    if (!cleaned || cleaned === '0') return 'N/A';
    // Premium Server (Text Verified): show +1 area code prefix
    const isPremium = serverName && serverName.toLowerCase().includes('premium');
    if (isPremium) {
      cleaned = cleaned.replace(/^\+/, '');
      return '+1' + cleaned;
    }
    if (!cleaned.startsWith('+') && cleaned.length >= 10) {
      cleaned = '+' + cleaned;
    }
    return cleaned;
  };

  // Format phone number for COPY: raw number only (no +1 for Premium Server)
  const formatPhoneCopy = (phone, serverName) => {
    if (!phone) return '';
    let cleaned = String(phone).replace(/['"]/g, '').trim();
    if (cleaned.includes(',') || cleaned.includes('phone_number')) {
      const parts = cleaned.split(',');
      cleaned = parts[0].trim().replace(/[^0-9+]/g, '');
    }
    cleaned = cleaned.replace(/[^0-9+]/g, '');
    if (!cleaned || cleaned === '0') return '';
    // Premium Server: copy raw number only (no country code)
    const isPremium = serverName && serverName.toLowerCase().includes('premium');
    if (isPremium) return cleaned.replace(/^\+/, '');
    if (!cleaned.startsWith('+') && cleaned.length >= 10) {
      cleaned = '+' + cleaned;
    }
    return cleaned;
  };

  // Map country code to readable name
  const getCountryName = (code) => {
    if (!code) return '';
    const map = {
      '187': 'US', 'usa': 'US', 'us': 'US',
      '0': 'RU', 'russia': 'RU',
      '86': 'NG', 'nigeria': 'NG',
      '16': 'GB', 'unitedkingdom': 'GB', 'england': 'GB',
      '36': 'CA', 'canada': 'CA',
      '22': 'IN', 'india': 'IN',
      '175': 'DE', 'germany': 'DE',
      '117': 'FR', 'france': 'FR',
      '76': 'BR', 'brazil': 'BR',
    };
    const key = String(code).toLowerCase().trim();
    return map[key] || key.toUpperCase();
  };



  const getCountryFlagUrl = (countryValue) => {
    if (!countryValue) return null;
    const key = String(countryValue).toLowerCase().replace(/\s+/g, '');

    // Common overrides
    const known = {
      usa: 'us',
      unitedstates: 'us',
      nigeria: 'ng',
      unitedkingdom: 'gb',
      uk: 'gb',
      canada: 'ca',
      india: 'in',
    };

    const iso2 = known[key] || (/^[a-z]{2}$/.test(key) ? key : null);
    if (!iso2) return null;

    // Use svg for better compatibility
    return `https://flagcdn.com/${iso2}.svg`;
  };

  // Update timer every second (for countdown in active orders)
  // Also poll for order updates to get OTP when received
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    
    // Poll for order updates every 5 seconds
    const pollInterval = setInterval(() => {
      if (fetchOrders) {
        fetchOrders();
      }
    }, 5000);
    
    return () => {
      clearInterval(interval);
      clearInterval(pollInterval);
    };
  }, [fetchOrders]);

  // Fetch provider status on mount
  useEffect(() => {
    const fetchProvidersStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/services/providers/status`, axiosConfig);
        if (response.data.success) {
          setProvidersStatus(response.data.providers);
        }
      } catch (error) {
        console.error('Failed to fetch provider status:', error);
      }
    };
    fetchProvidersStatus();
  }, [axiosConfig]);

  // Fetch services for selected provider (US Numbers or Other Countries mode)
  const fetchProviderServices = useCallback(async (providerId, isUSMode) => {
    if (!providerId) {
      setProviderServices([]);
      return;
    }

    setProviderServicesLoading(true);
    try {
      let endpoint = '';
      let params = {};
      
      if (isUSMode) {
        // US Numbers mode
        switch (providerId) {
          case 'textverified':
            endpoint = '/api/services/premium-server';
            break;
          case '5sim':
            endpoint = '/api/services/server-1';
            params = { country: 'usa' };
            break;
          case 'smsbower':
            endpoint = '/api/services/budget-server';
            params = { country: '187' };  // 187 = USA for SMS Bower
            break;
          case 'tigersms':
            endpoint = '/api/services/fast-server';
            params = { country: '187' };  // 187 = USA for Tiger SMS
            break;
          default:
            return;
        }

        const response = await axios.get(`${API}${endpoint}`, { ...axiosConfig, params });
        
        if (response.data.success) {
          let serviceList = response.data.services || [];
          
          // Fallback: Normalize service format for tigersms if old format returned
          if (providerId === 'tigersms' && !serviceList.length && response.data.data) {
            const usaServices = response.data.data['187'] || response.data.data['us'] || {};
            serviceList = Object.entries(usaServices).map(([code, info]) => ({
              value: code,
              label: info.name || code,
              name: info.name || code,
              price_ngn: parseFloat(info.cost || 0) * 1500 * 1.5,
              price_usd: parseFloat(info.cost || 0) * 1.5
            }));
          }
          
          setProviderServices(serviceList);
        } else {
          setProviderServices([]);
        }
      } else {
        // Other Countries mode - fetch countries first
        switch (providerId) {
          case '5sim':
            endpoint = '/api/services/server-1';
            break;
          case 'smsbower':
            endpoint = '/api/services/budget-server/countries';
            break;
          case 'tigersms':
            endpoint = '/api/services/fast-server/countries';
            break;
          default:
            return;
        }

        const response = await axios.get(`${API}${endpoint}`, axiosConfig);
        
        if (response.data.success && response.data.countries) {
          // Filter out USA for Other Countries mode
          const filtered = response.data.countries.filter(c => {
            const val = String(c.value || c.short_name || '').toLowerCase();
            return !['usa', 'us', '187', '0'].includes(val);
          });
          setProviderCountries(filtered);
        }
        setProviderServices([]);
      }
    } catch (error) {
      console.error('Failed to fetch provider services:', error);
      toast.error('Failed to load services');
      setProviderServices([]);
    } finally {
      setProviderServicesLoading(false);
    }
  }, [axiosConfig]);

  // Fetch services when country changes in Other Countries mode
  const fetchProviderServicesForCountry = useCallback(async (providerId, countryCode) => {
    if (!providerId || !countryCode) {
      setProviderServices([]);
      return;
    }

    setProviderServicesLoading(true);
    try {
      let endpoint = '';
      let params = { country: countryCode };
      
      switch (providerId) {
        case '5sim':
          endpoint = '/api/services/server-1';
          break;
        case 'smsbower':
          endpoint = '/api/services/budget-server';
          break;
        case 'tigersms':
          endpoint = '/api/services/fast-server';
          break;
        default:
          return;
      }

      const response = await axios.get(`${API}${endpoint}`, { ...axiosConfig, params });
      
      if (response.data.success && response.data.services) {
        setProviderServices(response.data.services);
      } else {
        setProviderServices([]);
      }
    } catch (error) {
      console.error('Failed to fetch services for country:', error);
      setProviderServices([]);
    } finally {
      setProviderServicesLoading(false);
    }
  }, [axiosConfig]);

  // Fetch operators/providers for a specific service (5sim and smsbower)
  const fetchServiceProviders = useCallback(async (providerId, serviceCode, countryCode) => {
    // Only for 5sim and smsbower
    if (!['5sim', 'smsbower'].includes(providerId)) {
      return [];
    }

    setProvidersLoading(true);
    try {
      let endpoint = '';
      let params = {};

      if (providerId === '5sim') {
        // 5sim uses the prices endpoint with country and product
        endpoint = '/api/services/server-1/operators';
        params = { country: countryCode || 'usa', service: serviceCode };
      } else if (providerId === 'smsbower') {
        // SMS Bower uses getPricesV3 which returns providers per service
        endpoint = '/api/services/budget-server/providers';
        params = { country: countryCode || '187', service: serviceCode };
      }

      const response = await axios.get(`${API}${endpoint}`, { ...axiosConfig, params });
      
      if (response.data.success && response.data.providers) {
        setServiceProviders(response.data.providers);
        return response.data.providers;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch service providers:', error);
      return [];
    } finally {
      setProvidersLoading(false);
    }
  }, [axiosConfig]);

  // Handle service selection - show provider modal for 5sim and smsbower
  const handleServiceSelect = async (service) => {
    if (!service || !selectedProvider) return;
    
    // For 5sim and smsbower, show the provider selection modal
    if (['5sim', 'smsbower'].includes(selectedProvider.id)) {
      setSelectedService(service);
      const isUSMode = selectedServer?.value === 'us_numbers';
      const countryCode = isUSMode 
        ? (selectedProvider.id === '5sim' ? 'usa' : '187')
        : selectedCountry?.value;
      
      await fetchServiceProviders(selectedProvider.id, service.value, countryCode);
      setShowProviderModal(true);
    } else {
      // For other providers, just select the service directly
      setSelectedService(service);
      setEstimatedPrice(enforceMinPrice(service.price_ngn));
    }
  };

  // Handle provider/operator selection from modal
  const handleProviderSelection = (provider) => {
    setSelectedServiceProvider(provider);
    setShowProviderModal(false);
    // Update estimated price based on selected provider
    if (provider.price_ngn) {
      setEstimatedPrice(enforceMinPrice(provider.price_ngn));
    }
  };

  // Handle provider card selection
  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setSelectedService(null);
    setSelectedCountry(null);
    setProviderServices([]);
    setProviderCountries([]);
    setEstimatedPrice(null);
    
    const isUSMode = selectedServer?.value === 'us_numbers';
    fetchProviderServices(provider.id, isUSMode);
  };

  // Handle purchase for provider cards mode
  const handleProviderPurchase = async () => {
    if (!selectedService || !selectedProvider) {
      toast.error('Please select a service');
      return;
    }

    // For 5sim and smsbower, require provider/operator selection
    if (['5sim', 'smsbower'].includes(selectedProvider.id) && !selectedServiceProvider) {
      toast.error('Please select a provider/operator');
      setShowProviderModal(true);
      return;
    }

    setPurchasing(true);
    try {
      const isUSMode = selectedServer?.value === 'us_numbers';
      const payload = {
        server: `${selectedProvider.id}_${isUSMode ? 'us' : 'global'}`,
        provider: selectedProvider.id,
        service: selectedService.value,
        service_name: selectedService.name || selectedService.label,
        country: isUSMode 
          ? (selectedProvider.id === '5sim' ? 'usa' : '187')
          : selectedCountry?.value,
        payment_currency: 'NGN',
        promo_code: promoCode || undefined
      };

      // Add operator for 5sim
      if (selectedProvider.id === '5sim' && selectedServiceProvider) {
        payload.operator = selectedServiceProvider.operator || selectedServiceProvider.name;
      }
      
      // Add provider_id for smsbower
      if (selectedProvider.id === 'smsbower' && selectedServiceProvider) {
        payload.provider_id = selectedServiceProvider.provider_id;
      }

      const response = await axios.post(`${API}/api/orders/purchase`, payload, axiosConfig);

      if (response.data.success) {
        toast.success('Number purchased successfully!');
        setSelectedService(null);
        setSelectedServiceProvider(null);
        setServiceProviders([]);
        fetchOrders();
        fetchProfile();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  // Fetch services for selected server
  const fetchServicesForServer = async (serverValue) => {
    if (!serverValue) {
      setAvailableServices([]);
      setAvailableCountries([]);
      return;
    }

    setServicesLoading(true);
    try {
      const serverMap = {
        us_server: 'us_server',
        server1: 'server1',
        server2: 'server2'
      };

      const endpoint = serverMap[serverValue];

      if (serverValue === 'us_server') {
        // US Server - US only, fetch services directly
        const response = await axios.get(`${API}/api/services/us_server`, axiosConfig);
        if (response.data.success) {
          const services = (response.data.services || []).map((service) => ({
            value: service.value,
            label: service.name,
            name: service.name,
            price_usd: service.final_price,
            price_ngn: service.final_price_ngn || service.final_price * 1500,
            count: service.count
          }));
          setAvailableServices(services);
          setAvailableCountries([{ value: '187', label: 'United States' }]);
        }
      } else if (serverValue === 'server1') {
        // Server 1 - fetch countries first
        const response = await axios.get(`${API}/api/services/server1`, axiosConfig);
        if (response.data.success && response.data.countries) {
          setAvailableCountries(response.data.countries);
          setAvailableServices([]); // Services loaded after country selection
        }
      } else {
        // Server 2 - Global server - first load countries
        const response = await axios.get(`${API}/api/services/server2`, axiosConfig);
        if (response.data.success && response.data.countries) {
          setAvailableCountries(response.data.countries);
          setAvailableServices([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch services:', error);
      toast.error('Failed to load services');
    } finally {
      setServicesLoading(false);
    }
  };

  // Fetch services when country changes
  useEffect(() => {
    const fetchServicesForCountry = async () => {
      // Server 1: load services for selected country
      if (selectedServer?.value === 'server1' && selectedCountry) {
        setServicesLoading(true);
        try {
          const response = await axios.get(
            `${API}/api/services/server1?country=${selectedCountry.value}`,
            axiosConfig
          );

          if (response.data.success && response.data.services) {
            setAvailableServices(response.data.services);
          }
        } catch (error) {
          console.error('Failed to fetch services for country:', error);
          toast.error('Failed to load services');
        } finally {
          setServicesLoading(false);
        }
      }

      // Server 2: load services/operators for selected country
      if (selectedServer?.value === 'server2' && selectedCountry) {
        setServicesLoading(true);
        try {
          const response = await axios.get(
            `${API}/api/services/server2?country=${selectedCountry.value}`,
            axiosConfig
          );
          if (response.data.success && response.data.services) {
            setAvailableServices(response.data.services);
          }
        } catch (error) {
          console.error('Failed to fetch services for country:', error);
          toast.error('Failed to load services');
        } finally {
          setServicesLoading(false);
        }
      }
    };

    fetchServicesForCountry();
    // IMPORTANT: Depend ONLY on selectedCountry and selectedServer so
    // services reload when country/server changes, not on every parent re-render.
  }, [selectedServer, selectedCountry]);

  // Calculate estimated price when selection changes
  useEffect(() => {
    const calculatePrice = async () => {
      if (!selectedServer || !selectedService) {
        setEstimatedPrice(null);
        return;
      }

      // For DaisySMS (US server), calculate price in NGN with advanced options
      // If promo code is entered, use backend to calculate discounted price
      if (selectedServer.value === 'us_server' && selectedService.price_ngn) {
        if (promoCode) {
          // When promo code exists, call backend to get discounted price
          try {
            const response = await axios.post(
              `${API}/api/orders/calculate-price`,
              {
                server: selectedServer.value,
                service: selectedService.value,
                country: '187', // DaisySMS is US-only
                promo_code: promoCode,
                area_code: selectedAreaCodes && selectedAreaCodes.length > 0 ? selectedAreaCodes[0].value : undefined,
                carrier: selectedCarrier?.value,
              },
              axiosConfig
            );

            if (response.data.success) {
              setEstimatedPrice({
                ...response.data,
                final_ngn: response.data.final_price_ngn,
                final_usd: response.data.final_price_usd,
              });
              return; // Exit early, price is set
            }
          } catch (error) {
            console.error('Failed to calculate promo price:', error);
            // Fall through to client-side calculation if API fails
          }
        }
        
        // For DaisySMS with advanced options, always use backend calculation
        // since the advanced markup is configurable from admin
        if (selectedCarrier || (selectedAreaCodes && selectedAreaCodes.length > 0) || preferredNumber) {
          try {
            const response = await axios.post(
              `${API}/api/orders/calculate-price`,
              {
                server: selectedServer.value,
                service: selectedService.value,
                country: '187', // DaisySMS is US-only
                area_code: selectedAreaCodes && selectedAreaCodes.length > 0 ? selectedAreaCodes[0].value : undefined,
                carrier: selectedCarrier?.value,
              },
              axiosConfig
            );

            if (response.data.success) {
              setEstimatedPrice({
                ...response.data,
                final_ngn: response.data.final_price_ngn,
                final_usd: response.data.final_price_usd,
              });
              return;
            }
          } catch (error) {
            console.error('Failed to calculate advanced price:', error);
          }
        }
        
        // Client-side calculation (no advanced options)
        let baseNGN = selectedService.price_ngn;
        setEstimatedPrice({
          final_usd: selectedService.price_usd,
          final_ngn: baseNGN,
          breakdown: [`Base: ₦${baseNGN.toFixed(2)}`]
        });
      } else if (selectedServer.value === 'server1' && selectedService.price_ngn) {
        // SMS-pool (International server): allow user to select a specific pool.
        let poolToUse = null;
        if (selectedService.pools && selectedService.pools.length > 0) {
          // If a specific pool is selected, use that; otherwise default to the
          // cheapest pool (already reflected in price_ngn on the service).
          poolToUse = selectedPool || null;
        }

        const baseNGN = poolToUse?.price_ngn || selectedService.price_ngn;
        const breakdown = [`Base (selected pool): ₦${baseNGN.toFixed(2)}`];

        if (selectedService.pools && selectedService.pools.length > 0) {
          breakdown.push(`Pools available: ${selectedService.pools.length}`);
        }

        setEstimatedPrice({
          final_usd: null,
          final_ngn: baseNGN,
          breakdown
        });
      } else if (selectedCountry && selectedServer.value !== 'server2') {
        // For DaisySMS and SMS-pool, use backend price calculator
        try {
          const response = await axios.post(
            `${API}/api/orders/calculate-price`,
            {
              server: selectedServer.value,
              service: selectedService.value,
              country: selectedCountry?.value,
              promo_code: promoCode || undefined,
              area_code: selectedAreaCodes && selectedAreaCodes.length > 0 ? selectedAreaCodes[0].value : undefined,
              carrier: selectedCarrier?.value,
            },
            axiosConfig
          );

          if (response.data.success) {
            // Map backend response to frontend expected format
            setEstimatedPrice({
              ...response.data,
              final_ngn: response.data.final_price_ngn,
              final_usd: response.data.final_price_usd,
            });
          }
        } catch (error) {
          console.error('Failed to calculate price:', error);
        }
      } else if (selectedServer.value === 'server2' && selectedService && selectedCountry) {
        // For 5sim, backend already returns NGN price per service/operator
        const baseNGN = selectedOperator?.price_ngn || selectedService.price_ngn || 0;
        setEstimatedPrice({
          final_usd: selectedService.price_usd || null,
          final_ngn: baseNGN,
          breakdown: [`Base: ₦${baseNGN.toFixed(2)}`],
        });
      }
    };

    calculatePrice();
  }, [
    selectedServer,
    selectedService,
    selectedCountry,
    selectedCarrier,
    selectedAreaCodes,
    preferredNumber,
    promoCode,
    axiosConfig
  ]);

  const handlePurchaseNumber = async () => {
    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }

    // Balance check is still done by backend; client-side check can be misleading with promo discounts.

    setPurchasing(true);
    try {
      const payload = {
        server: selectedServer.value,
        service: selectedService.value,
        service_name: selectedService.name || selectedService.label,
        // For now DaisySMS is US-only so country is fixed to 187;
        // for other providers, country comes from selectedCountry
        country: selectedServer.value === 'us_server' ? '187' : selectedCountry?.value,
        promo_code: promoCode || undefined,
        area_code: selectedAreaCodes && selectedAreaCodes.length > 0 ? selectedAreaCodes[0].value : undefined,
        carrier: selectedCarrier?.value
      };

      // For SMS-pool, include selected pool (if any)
      if (selectedServer.value === 'server1' && selectedPool) {
        payload.pool = selectedPool.id;
      }

      // For 5sim, include selected operator (if any)
      if (selectedServer.value === 'server2' && selectedOperator) {
        payload.operator = selectedOperator.value;
      }

      // Add advanced options if selected (DaisySMS only)
      if (selectedCarrier) {
        payload.carrier = selectedCarrier.value;
      }
      if (selectedAreaCodes && selectedAreaCodes.length > 0) {
        payload.area_codes = selectedAreaCodes.map((a) => a.value).join(',');
      }
      if (preferredNumber) {
        payload.preferred_number = preferredNumber;
      }

      const response = await axios.post(`${API}/api/orders/purchase`, payload, axiosConfig);

      if (response.data.success) {
        toast.success('Number purchased successfully!');
        setSelectedService(null);
        setSelectedCarrier(null);
        setSelectedAreaCodes([]);
        setPreferredNumber('');
        setShowAdvancedOptions(false);
        fetchOrders();
        fetchProfile();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      const response = await axios.post(`${API}/api/orders/${orderId}/cancel`, {}, axiosConfig);
      if (response.data.success) {
        const refunded = response.data.refunded?.toFixed(2) || '0.00';
        toast.success(`Order cancelled and refunded ₦${refunded}`);
        fetchOrders();
        fetchProfile();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Cancel failed');
    }
  };

  const copyToClipboard = async (text, message = 'Copied to clipboard!') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(message);
    } catch (err) {
      console.error('Clipboard write failed', err);
      toast.error('Unable to copy to clipboard in this browser.');
    }
  };

  const copyOTP = (code) => {
    copyToClipboard(code, 'OTP copied to clipboard!');
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="mb-1">
        <p className="text-[13px] text-muted-foreground">Services &middot; SMS verification</p>
        <h1 className="mt-1 text-[clamp(28px,3.2vw,38px)] font-extrabold leading-[1.08] tracking-[-.025em]">Buy numbers</h1>
      </div>

      {/* Collapsible Tips Sections */}
      <div className="space-y-2">
        {/* Issues Tips */}
        <div className="rounded-2xl bg-card shadow-card overflow-hidden">
          <button
            onClick={() => setIssuesTipOpen(!issuesTipOpen)}
            className="flex min-h-12 w-full items-center gap-3 px-3.5 text-left font-bold text-sm transition hover:bg-muted"
          >
            <div className="flex items-center gap-3 min-w-0">
              <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
              <span className="font-semibold text-foreground text-sm">Read if you're encountering issues</span>
            </div>
            <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${issuesTipOpen ? 'rotate-180' : ''}`} />
          </button>
          {issuesTipOpen && (
            <div className="px-4 pb-4 pt-1 text-xs text-muted-foreground space-y-2 border-t border-border">
              <p className="font-semibold text-foreground">Not receiving OTP/Code?</p>
              <p>• If you don't receive any OTP/code after 2-7 mins, just cancel the previous number and request for another number.</p>
              <p>• If you buy any number and it shows that it has been banned (Mostly: WhatsApp/Telegram), just cancel and request for another number.</p>
            </div>
          )}
        </div>

        {/* Buy Tips */}
        <div className="rounded-2xl bg-card shadow-card overflow-hidden">
          <button
            onClick={() => setBuyTipsOpen(!buyTipsOpen)}
            className="flex min-h-12 w-full items-center gap-3 px-3.5 text-left font-bold text-sm transition hover:bg-muted"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Info className="w-4 h-4 shrink-0 text-primary" />
              <span className="font-semibold text-foreground text-sm">Read before you buy numbers</span>
            </div>
            <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${buyTipsOpen ? 'rotate-180' : ''}`} />
          </button>
          {buyTipsOpen && (
            <div className="px-4 pb-4 pt-1 text-xs text-muted-foreground space-y-3 border-t border-border">
              <div>
                <p className="font-semibold text-foreground mb-1">Telegram tips</p>
                <p>• Telegram numbers have only 45% percent rate to receive code.</p>
                <p>• 50/50 to receive code on telegram numbers.</p>
                <p>• Add Two 2FA authentication after purchasing.</p>
              </div>
              <div>
                <p className="font-semibold text-foreground mb-1">WhatsApp tips</p>
                <p>• 50/50 chance for using WhatsApp Business.</p>
                <p>• Don't text immediately with WhatsApp after purchasing.</p>
                <p>• Add Two 2FA authentication after purchasing.</p>
                <p>• Delete and install back your WhatsApp before buying.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Server Selection — design handoff: always-visible server cards, not a dropdown.
          Values, onChange behaviour and fetchServicesForServer are unchanged. */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[.08em] text-muted-foreground mb-2.5">1 &middot; Choose a server</p>
        <div className="flex flex-col gap-2">
          {SERVER_OPTIONS.map((opt) => {
            const isSelected = selectedServer?.value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                data-testid={`server-card-${opt.value}`}
                onClick={() => {
                  const option = isSelected ? null : opt;
                  setSelectedServer(option);
                  setSelectedService(null);
                  setSelectedCountry(null);
                  setEstimatedPrice(null);
                  setSelectedProvider(null);
                  setProviderServices([]);
                  setProviderCountries([]);
                  if (option) {
                    if (option.value === 'us_numbers' || option.value === 'other_countries') {
                      // Provider cards mode - don't fetch services yet
                    } else {
                      fetchServicesForServer(option.value);
                    }
                  } else {
                    setAvailableServices([]);
                    setAvailableCountries([]);
                  }
                }}
                className={`grid w-full grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3.5 rounded-2xl bg-card p-3 text-left shadow-card transition hover:bg-muted ${
                  isSelected ? 'ring-2 ring-primary' : ''
                }`}
              >
                <span className={`grid size-11 place-items-center rounded-xl ${
                  isSelected ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                }`}>
                  <opt.icon className="size-[18px]" />
                </span>
                <span className="min-w-0">
                  <span className="block font-bold">{opt.label}</span>
                  <span className="block truncate text-[13px] text-muted-foreground">{opt.desc}</span>
                </span>
                {opt.badge && (
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide bg-accent text-accent-foreground">
                    {opt.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Provider Cards - Show for US Numbers and Other Countries */}
      {selectedServer && (selectedServer.value === 'us_numbers' || selectedServer.value === 'other_countries') && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
          {/* Provider Cards Grid */}
          <div className="space-y-2">
            {(selectedServer.value === 'us_numbers' ? US_PROVIDERS : OTHER_COUNTRIES_PROVIDERS).map((provider) => {
              const status = providersStatus[provider.id];
              const isEnabled = status?.enabled !== false;
              const isSelected = selectedProvider?.id === provider.id;

              return (
                <button
                  key={provider.id}
                  data-testid={`provider-card-${provider.id}`}
                  onClick={() => isEnabled && handleProviderSelect(provider)}
                  disabled={!isEnabled}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                    !isEnabled
                      ? 'bg-card border-border cursor-not-allowed opacity-50'
                      : isSelected
                        ? 'border-primary ring-1 ring-primary bg-primary/5'
                        : 'border-border bg-card hover:border-primary/40'
                  }`}
                >
                  <span className={`w-10 h-10 shrink-0 grid place-items-center rounded-md ${
                    isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Server className="w-4 h-4" />
                  </span>

                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold text-foreground truncate">{provider.name}</span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {[provider.description, ...provider.features.slice(0, 2)].join(' · ')}
                    </span>
                  </span>

                  <span className="shrink-0 flex items-center gap-2">
                    {!isEnabled && (
                      <span className="text-[10px] font-semibold text-muted-foreground">Unavailable</span>
                    )}
                    <span className={`px-2 py-0.5 text-[9px] font-bold rounded-full ${
                      isSelected ? 'bg-primary text-primary-foreground' : 'bg-accent/15 text-accent'
                    }`}>
                      {provider.badge}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Service Selection for Provider Cards */}
          {selectedProvider && (
            <div className="rounded-3xl bg-card shadow-card flex flex-col gap-4 p-5">
              <div className="flex items-center gap-2">
                <span className="text-xl">{selectedProvider.icon}</span>
                <h3 className="font-semibold text-foreground text-sm">{selectedProvider.name}</h3>
              </div>

              {/* Country Dropdown - Only for Other Countries mode */}
              {selectedServer.value === 'other_countries' && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Select Country
                  </label>
                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={selectedCountry}
                    onChange={(option) => {
                      setSelectedCountry(option);
                      setSelectedService(null);
                      setEstimatedPrice(null);
                      if (option) {
                        fetchProviderServicesForCountry(selectedProvider.id, option.value);
                      } else {
                        setProviderServices([]);
                      }
                    }}
                    options={providerCountries}
                    isLoading={providerServicesLoading && providerCountries.length === 0}
                    isDisabled={providerServicesLoading && providerCountries.length === 0}
                    placeholder="Select a country..."
                    isClearable
                    isSearchable
                    formatOptionLabel={(option) => {
                      const flagUrl = getCountryFlagUrl(option.value || option.short_name || option.label);
                      return (
                        <div className="flex items-center gap-2">
                          {flagUrl && (
                            <img
                              src={flagUrl}
                              alt={option.label}
                              className="w-5 h-4 rounded border border-border object-cover"
                            />
                          )}
                          <span className="text-xs">{option.label || option.name}</span>
                        </div>
                      );
                    }}
                  />
                </div>
              )}

              {/* Service Selection */}
              {(selectedServer.value === 'us_numbers' || selectedCountry) && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Select Service
                  </label>
                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    menuPlacement="auto"
                    value={selectedService}
                    onChange={async (option) => {
                      if (option) {
                        setSelectedService(option);
                        setSelectedServiceProvider(null); // Reset provider selection
                        
                        // For 5sim and smsbower, automatically load providers and show modal
                        if (['5sim', 'smsbower'].includes(selectedProvider?.id)) {
                          const isUSMode = selectedServer?.value === 'us_numbers';
                          const countryCode = isUSMode 
                            ? (selectedProvider.id === '5sim' ? 'usa' : '187')
                            : selectedCountry?.value;
                          
                          await fetchServiceProviders(selectedProvider.id, option.value, countryCode);
                          setShowProviderModal(true);
                        } else {
                          // For other providers, just set the price
                          setEstimatedPrice({
                            price_ngn: option.price_ngn || option.final_price_ngn || 0,
                            price_usd: option.price_usd || option.final_price || 0
                          });
                        }
                      } else {
                        setSelectedService(null);
                        setSelectedServiceProvider(null);
                        setEstimatedPrice(null);
                      }
                    }}
                    options={providerServices}
                    isLoading={providerServicesLoading}
                    isDisabled={providerServicesLoading}
                    placeholder={providerServicesLoading ? 'Loading services...' : 'Search for a service...'}
                    isClearable
                    isSearchable
                    formatOptionLabel={(option) => (
                      <div className="flex items-center justify-between w-full">
                        <span className="text-xs font-medium">{option.label || option.name || getServiceName(option.value)}</span>
                        {/* Show price only for non-5sim/smsbower providers */}
                        {!['5sim', 'smsbower'].includes(selectedProvider?.id) && option.price_ngn && (
                          <span className="text-primary font-semibold text-xs">
                            ₦{enforceMinPrice(option.price_ngn).toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              )}

              {/* Price Display */}
              {estimatedPrice && (estimatedPrice.price_ngn > 0 || estimatedPrice.promo) && (
                <div className="bg-gradient-to-br from-emerald-50 to-white border border-primary rounded-lg p-2.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="block text-[10px] font-semibold text-primary uppercase">Total Cost</span>
                    </div>
                    <div className="text-right">
                      <span className="block text-lg font-bold text-primary">
                        ₦{enforceMinPrice(estimatedPrice.price_ngn)?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {estimatedPrice.promo && (
                    <div className="flex items-center justify-between bg-primary/10 border border-primary rounded-md px-2 py-1 mt-1.5">
                      <span className="text-[10px] font-semibold text-primary">
                        Promo &quot;{estimatedPrice.promo.code}&quot; applied!
                      </span>
                      <span className="text-[10px] font-bold text-primary">
                        -₦{estimatedPrice.promo.discount_ngn?.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Selected Provider/Operator Display (for 5sim and smsbower) */}
              {['5sim', 'smsbower'].includes(selectedProvider?.id) && selectedService && selectedServiceProvider && (
                <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-semibold text-primary uppercase block mb-1">Selected Operator</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-primary text-sm">
                          {selectedServiceProvider.name || selectedServiceProvider.operator || `Provider ${selectedServiceProvider.provider_id}`}
                        </span>
                        {selectedServiceProvider.delivery_rate > 0 && (
                          <span className="flex items-center gap-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                            <TrendingUp className="w-3 h-3" />
                            {selectedServiceProvider.delivery_rate}%
                          </span>
                        )}
                      </div>
                      <span className="text-primary font-bold text-sm">
                        ₦{enforceMinPrice(selectedServiceProvider.price_ngn)?.toFixed(2)}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowProviderModal(true)}
                      className="px-3 py-1.5 bg-primary text-white text-xs font-semibold rounded-md hover:bg-primary/90 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                </div>
              )}

              {/* Prompt to select operator for 5sim/smsbower - now shows loading or re-open modal */}
              {['5sim', 'smsbower'].includes(selectedProvider?.id) && selectedService && !selectedServiceProvider && (
                <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
                  {providersLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 text-muted-foreground animate-spin" />
                      <span className="text-muted-foreground text-xs">Loading operators...</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowProviderModal(true)}
                      className="px-4 py-2 bg-primary text-white text-xs font-semibold rounded-md hover:bg-primary transition-colors"
                    >
                      Select Operator
                    </button>
                  )}
                </div>
              )}

              {/* Promo Code */}
              {selectedService && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Promo Code (optional)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Enter promo code"
                      value={promoCode || ''}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        if (estimatedPrice?.promo) {
                          setEstimatedPrice(prev => typeof prev === 'object' ? { ...prev, promo: null } : prev);
                        }
                      }}
                      data-testid="provider-promo-input"
                      className={`flex-1 px-3 py-2 border rounded-md focus:outline-none text-xs text-foreground ${
                        estimatedPrice?.promo
                          ? 'border-primary bg-primary/10'
                          : 'border-border focus:border-primary'
                      }`}
                    />
                    {promoCode && (
                      <button
                        onClick={async () => {
                          if (!selectedService || !selectedProvider) {
                            toast.error('Please select a service first');
                            return;
                          }
                          try {
                            const isUSMode = selectedServer?.value === 'us_numbers';
                            const countryCode = isUSMode
                              ? (selectedProvider.id === '5sim' ? 'usa' : selectedProvider.id === 'smsbower' ? '187' : '187')
                              : selectedCountry?.value;
                            const response = await axios.post(
                              `${API}/api/orders/calculate-price`,
                              {
                                server: `${selectedProvider.id}_${isUSMode ? 'us' : 'global'}`,
                                service: selectedService.value,
                                country: countryCode,
                                promo_code: promoCode,
                              },
                              axiosConfig
                            );
                            if (response.data.success && response.data.promo) {
                              setEstimatedPrice({
                                price_ngn: response.data.final_price_ngn,
                                price_usd: response.data.final_price_usd,
                                promo: response.data.promo,
                              });
                              toast.success(`Promo "${promoCode}" applied! You save ₦${response.data.promo.discount_ngn.toFixed(2)}`);
                            } else if (response.data.success) {
                              toast.error('Invalid or expired promo code');
                            }
                          } catch (error) {
                            let errorMsg = 'Invalid promo code';
                            const detail = error.response?.data?.detail;
                            if (typeof detail === 'string') errorMsg = detail;
                            else if (Array.isArray(detail) && detail.length > 0) errorMsg = detail[0]?.msg || 'Validation error';
                            toast.error(errorMsg);
                          }
                        }}
                        data-testid="provider-promo-apply-btn"
                        className="px-3 py-2 bg-primary text-white rounded-md font-semibold hover:bg-primary transition-colors text-xs"
                      >
                        Apply
                      </button>
                    )}
                  </div>
                  {estimatedPrice?.promo && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-primary text-[10px]">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">Promo &quot;{estimatedPrice.promo.code}&quot; applied! Saving ₦{estimatedPrice.promo.discount_ngn?.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Purchase Button */}
              <button
                onClick={handleProviderPurchase}
                disabled={!selectedService || purchasing || (['5sim', 'smsbower'].includes(selectedProvider?.id) && !selectedServiceProvider)}
                data-testid="purchase-provider-btn"
                className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground font-bold text-sm shadow-cta hover:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Phone className="w-4 h-4" />
                {purchasing ? 'Purchasing...' : 'Purchase Number'}
              </button>
            </div>
          )}

          {/* Empty State */}
          {!selectedProvider && (
            <div className="text-center py-6 text-muted-foreground bg-card rounded-lg border">
              <Phone className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-xs">Select a provider to get started</p>
            </div>
          )}
        </div>
      )}

      {/* Purchase New Number - Only for original servers */}
      {selectedServer && !['us_numbers', 'other_countries'].includes(selectedServer.value) && (
      <div className="rounded-2xl bg-card shadow-card overflow-hidden">
        <button
          onClick={() => setPurchaseExpanded(!purchaseExpanded)}
          className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Plus
              className={`w-3 h-3 sm:w-4 sm:h-4 text-primary transition-transform ${
                purchaseExpanded ? 'rotate-45' : ''
              }`}
            />
            <h3 className="text-xs sm:text-sm font-semibold text-foreground">Purchase Number</h3>
          </div>
          <ChevronDown
            className={`w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground transition-transform ${
              purchaseExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>

        {purchaseExpanded && (
          <div className="p-3 sm:p-4 pt-0 space-y-2.5 border-t">
            {/* Country Selection - Show for International & Global servers */}
            {selectedServer &&
              (selectedServer.value === 'server1' || selectedServer.value === 'server2') && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Select Country
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={selectedCountry}
                    onChange={(option) => {
                      setSelectedCountry(option);
                      setSelectedService(null); // Reset service when country changes
                    }}
                    options={availableCountries}
                    formatOptionLabel={(option) => {
                      const flagUrl = getCountryFlagUrl(option.value || option.short_name || option.label);
                      return (
                        <div className="flex items-center gap-2">
                          {flagUrl && (
                            <img
                              src={flagUrl}
                              alt={option.label}
                              className="w-5 h-5 rounded-full border border-border object-cover"
                            />
                          )}
                          <span className="text-xs font-semibold text-foreground">{option.label}</span>
                        </div>
                      );
                    }}
                    isDisabled={!selectedServer || servicesLoading}
                    isLoading={servicesLoading}
                    placeholder="Choose country..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isClearable
                    isSearchable
                  />
                </div>
              )}

            {/* Service Search - Show after country is selected (or for US server which doesn't need country) */}
            {selectedServer && ((selectedServer.value === 'us_server') || selectedCountry) && (
              <div className="space-y-2">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Search Service
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      ...selectStyles,
                      control: (base) => ({
                        ...base,
                        minHeight: '48px',
                        borderWidth: '2px',
                        borderColor: 'hsl(var(--input))',
                        borderRadius: 12,
                        fontSize: '0.75rem',
                        '&:hover': { borderColor: 'hsl(var(--primary))' }
                      }),
                    }}
                    value={selectedService}
                    onMenuOpen={() => setServiceMenuOpen(true)}
                    onMenuClose={() => setServiceMenuOpen(false)}
                    onInputChange={(value, { action }) => {
                      // Only keep menu open while typing, avoid closing on blur
                      if (action === 'input-change' && !serviceMenuOpen) {
                        setServiceMenuOpen(true);
                      }
                    }}
                    menuIsOpen={serviceMenuOpen}
                    onChange={(option) => {
                      setSelectedService(option);
                      setSelectedPool(null);
                    }}
                    options={availableServices}
                    isDisabled={servicesLoading}
                    isLoading={servicesLoading}
                    placeholder={
                      servicesLoading ? 'Loading...' : 'Search service...'
                    }
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isClearable
                    isSearchable
                    formatOptionLabel={(option) => (
                      <div className="flex items-center justify-between w-full">
                        <div className="flex flex-col">
                          <span className="text-[11px] sm:text-xs">{option.label || option.name}</span>
                          {option.pools && option.pools.length > 0 && (
                            <span className="text-[9px] text-muted-foreground">
                              {option.pools.length} pool{option.pools.length > 1 ? 's' : ''}
                            </span>
                          )}
                          {option.operators && option.operators.length > 0 && (
                            <span className="text-[9px] text-muted-foreground">
                              {option.operators.length} operator{option.operators.length > 1 ? 's' : ''}
            {/* 5sim Operator selection (similar to pools) */}
            {selectedServer?.value === 'server2' && selectedService?.operators && selectedService.operators.length > 0 && (
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Select Operator
                </label>
                <Select
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  value={selectedOperator}
                  onChange={(option) => setSelectedOperator(option)}
                  options={selectedService.operators.map((op) => ({
                    value: op.name,
                    label: `${op.name} (₦${enforceMinPrice(op.price_ngn).toFixed(2)})`,
                    price_ngn: enforceMinPrice(op.price_ngn)
                  }))}
                  isDisabled={!selectedService}
                  placeholder="Choose operator (default is cheapest)"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                />
              </div>
            )}

                            </span>
                          )}
                        </div>
                        {option.price_ngn && (
                          <span className="text-muted-foreground font-semibold text-xs">
                            ₦{enforceMinPrice(option.price_ngn).toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>

                {/* SMS-pool: allow user to pick a specific pool for the selected service */}
                {selectedServer.value === 'server1' && selectedService && selectedService.pools &&
                  selectedService.pools.length > 0 && (
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                        Select Pool (optional)
                      </label>
                      <Select
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                        value={selectedPool && {
                          value: selectedPool.id,
                          label: `${selectedPool.name} - ₦${enforceMinPrice(selectedPool.price_ngn).toFixed(2)}`
                        }}
                        onChange={(option) => {
                          if (!option) {
                            setSelectedPool(null);
                            return;
                          }
                          const poolObj = selectedService.pools.find(
                            (p) => String(p.id) === String(option.value)
                          ) || null;
                          setSelectedPool(poolObj);
                        }}
                        options={selectedService.pools.map((p) => ({
                          value: p.id,
                          label: `${p.name} - ₦${enforceMinPrice(p.price_ngn).toFixed(2)}`
                        }))}
                        placeholder="Cheapest pool (default)"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isClearable
                      />
                    </div>
                  )}
              </div>
            )}

            {/* Advanced Options Toggle - US Server Only */}
            {selectedServer && selectedServer.value === 'us_server' && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-primary/10 border border-primary/30 rounded-md hover:bg-primary/10 transition-colors"
                >
                  <span className="text-[10px] sm:text-xs font-semibold text-primary">
                    {showAdvancedOptions ? '▼' : '▶'} Advanced Options
                  </span>
                  <span className="text-[9px] sm:text-[10px] text-primary">+35% each</span>
                </button>
              </div>
            )}

            {/* Advanced Options Fields */}
            {showAdvancedOptions && selectedServer && selectedServer.value === 'us_server' && (
              <div className="bg-primary/10 rounded-lg p-3 space-y-3 border border-primary/30">
                {/* Carrier Selection */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Carrier <span className="text-primary">+35%</span>
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={selectedCarrier}
                    onChange={(option) => setSelectedCarrier(option)}
                    options={[
                      { value: 'tmo', label: 'T-Mobile' },
                      { value: 'vz', label: 'Verizon' },
                      { value: 'att', label: 'AT&T' }
                    ]}
                    placeholder="Any carrier"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isClearable
                  />
                </div>

                {/* Area Codes */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Area Codes <span className="text-primary">+35%</span>
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={selectedAreaCodes}
                    onChange={(options) => setSelectedAreaCodes(options || [])}
                    options={[
                      { value: '212', label: '212 - New York' },
                      { value: '718', label: '718 - New York' },
                      { value: '213', label: '213 - Los Angeles' },
                      { value: '310', label: '310 - Los Angeles' },
                      { value: '312', label: '312 - Chicago' },
                      { value: '773', label: '773 - Chicago' },
                      { value: '415', label: '415 - San Francisco' },
                      { value: '305', label: '305 - Miami' },
                      { value: '713', label: '713 - Houston' },
                      { value: '202', label: '202 - Washington DC' }
                    ]}
                    placeholder="Any area code"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isMulti
                    isClearable
                  />
                </div>

                {/* Preferred Number */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Preferred Number <span className="text-primary">+35%</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 11112223344"
                    value={preferredNumber}
                    onChange={(e) =>
                      setPreferredNumber(e.target.value.replace(/\D/g, '').slice(0, 11))
                    }
                    maxLength={11}
                    className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:outline-none text-xs text-foreground"
                  />
                  <p className="text-[9px] text-muted-foreground mt-0.5">Enter full number without +1</p>
                </div>
              </div>
            )}

            {/* Promo Code with Validation */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Promo Code (optional)</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Enter promo code"
                  value={promoCode || ''}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase());
                    // Reset estimated price to recalculate with new promo code
                    if (estimatedPrice?.promo) {
                      setEstimatedPrice(prev => ({ ...prev, promo: null }));
                    }
                  }}
                  className={`flex-1 px-3 py-2 border rounded-md focus:outline-none text-xs text-foreground ${
                    estimatedPrice?.promo 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border focus:border-primary'
                  }`}
                />
                {promoCode && (
                  <button
                    onClick={async () => {
                      if (!selectedService || !selectedServer) {
                        toast.error('Please select a service first');
                        return;
                      }
                      // Trigger price recalculation which validates promo
                      try {
                        const response = await axios.post(
                          `${API}/api/orders/calculate-price`,
                          {
                            server: selectedServer.value,
                            service: selectedService.value,
                            // For US server (DaisySMS), country is always 187
                            country: selectedServer.value === 'us_server' ? '187' : selectedCountry?.value,
                            promo_code: promoCode,
                            area_code: selectedAreaCodes && selectedAreaCodes.length > 0 ? selectedAreaCodes[0].value : undefined,
                            carrier: selectedCarrier?.value,
                          },
                          axiosConfig
                        );
                        if (response.data.success && response.data.promo) {
                          // Map backend response to frontend expected format
                          setEstimatedPrice({
                            ...response.data,
                            final_ngn: response.data.final_price_ngn,
                            final_usd: response.data.final_price_usd,
                          });
                          toast.success(`Promo "${promoCode}" applied! You save ₦${response.data.promo.discount_ngn.toFixed(2)}`);
                        } else if (response.data.success) {
                          setEstimatedPrice({
                            ...response.data,
                            final_ngn: response.data.final_price_ngn,
                            final_usd: response.data.final_price_usd,
                          });
                          toast.error('Invalid or expired promo code');
                        }
                      } catch (error) {
                        // Handle error - detail might be a string or an array of validation errors
                        let errorMsg = 'Invalid promo code';
                        const detail = error.response?.data?.detail;
                        if (typeof detail === 'string') {
                          errorMsg = detail;
                        } else if (Array.isArray(detail) && detail.length > 0) {
                          errorMsg = detail[0]?.msg || 'Validation error';
                        } else if (detail && typeof detail === 'object' && detail.msg) {
                          errorMsg = detail.msg;
                        }
                        toast.error(errorMsg);
                      }
                    }}
                    className="px-3 py-2 bg-primary text-white rounded-md font-semibold hover:bg-primary transition-colors text-xs"
                  >
                    Apply
                  </button>
                )}
              </div>
              {estimatedPrice?.promo && (
                <div className="mt-1.5 flex items-center gap-1.5 text-primary text-[10px]">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">Promo &quot;{estimatedPrice.promo.code}&quot; applied! Saving ₦{estimatedPrice.promo.discount_ngn?.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Price Display */}
            {estimatedPrice && (
              <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-white border border-primary rounded-lg p-3 space-y-2 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="block text-[10px] font-semibold text-primary uppercase tracking-wide">
                      Total Cost
                    </span>
                    <span className="block text-[9px] text-muted-foreground">
                      Includes all fees
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="block text-lg sm:text-xl font-extrabold text-primary leading-tight">
                      ₦{estimatedPrice.final_ngn?.toFixed(2)}
                    </span>
                    {estimatedPrice.final_usd && (
                      <span className="block text-[10px] text-muted-foreground">
                        ≈ ${estimatedPrice.final_usd.toFixed(2)} USD
                      </span>
                    )}
                  </div>
                </div>

                {/* Promo Applied Badge */}
                {estimatedPrice.promo && (
                  <div className="flex items-center justify-between bg-primary/10 border border-primary rounded-md px-2 py-1.5">
                    <div className="flex items-center gap-1">
                      <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-[10px] font-semibold text-primary">
                        Promo &quot;{estimatedPrice.promo.code}&quot; applied!
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-primary">
                      -₦{estimatedPrice.promo.discount_ngn?.toFixed(2)}
                    </span>
                  </div>
                )}

                {/* Show selected pool for SMS-pool when chosen */}
                {selectedServer?.value === 'server1' && selectedService && selectedService.pools && (
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>Pool:</span>
                    <span className="font-semibold">
                      {selectedPool
                        ? `${selectedPool.name} (₦${enforceMinPrice(selectedPool.price_ngn).toFixed(2)})`
                        : 'Cheapest available'}
                    </span>
                  </div>
                )}

                {estimatedPrice.breakdown && estimatedPrice.breakdown.length > 0 && (
                  <div className="pt-1.5 border-t border-primary">
                    <p className="text-[9px] text-muted-foreground font-semibold mb-0.5">Price breakdown</p>
                    <div className="space-y-0.5">
                      {estimatedPrice.breakdown.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-[9px] text-muted-foreground"
                        >
                          <span className="truncate max-w-[70%]">• {item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-[9px] text-muted-foreground">
                  💡 Paid from NGN balance only. Convert USD to NGN if needed.
                </p>
              </div>
            )}

            {/* Purchase Button */}
            <button
              onClick={handlePurchaseNumber}
              disabled={!selectedService || !estimatedPrice || purchasing}
              className="w-full py-2.5 bg-primary text-white rounded-full font-semibold text-xs hover:bg-primary transition-colors flex items-center justify-center gap-1.5 disabled:bg-muted disabled:cursor-not-allowed"
            >
              <Phone className="w-3.5 h-3.5" />
              {purchasing ? 'Purchasing...' : 'Purchase Number'}
            </button>
          </div>
        )}
      </div>
      )}

      {/* Your Verifications - Mobile-first responsive design */}
      <div className="rounded-2xl bg-card shadow-card p-3 sm:p-4 md:p-6">
        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-foreground mb-3 sm:mb-4">Your Verifications</h3>

        {orders.filter((o) => {
          // Show active orders OR completed orders with OTP (within last 10 minutes)
          if (o.status === 'active') return true;
          if ((o.status === 'completed' || o.status === 'received') && (o.otp || o.otp_code)) {
            const createdAt = new Date(o.created_at);
            const now = new Date();
            const elapsedMinutes = (now - createdAt) / (1000 * 60);
            return elapsedMinutes < 10; // Show for 10 minutes after creation
          }
          return false;
        }).length > 0 ? (
          <>
            {/* Mobile Card Layout */}
            <div className="block md:hidden space-y-3">
              {orders
                .filter((o) => {
                  if (o.status === 'active') return true;
                  if ((o.status === 'completed' || o.status === 'received') && (o.otp || o.otp_code)) {
                    const createdAt = new Date(o.created_at);
                    const now = new Date();
                    const elapsedMinutes = (now - createdAt) / (1000 * 60);
                    return elapsedMinutes < 10;
                  }
                  return false;
                })
                .map((order) => {
                  const createdAt = new Date(order.created_at);
                  const now = new Date();
                  const elapsedSeconds = Math.floor((now - createdAt) / 1000);
                  const timerDuration = getTimerDuration(order);
                  const remainingSeconds = Math.max(0, timerDuration - elapsedSeconds);
                  const minutes = Math.floor(remainingSeconds / 60);
                  const seconds = remainingSeconds % 60;
                  const hasOTP = order.otp || order.otp_code;
                  const canCancel = !hasOTP && remainingSeconds > 0;

                  return (
                    <div key={order.id} className={`rounded-lg p-3 border ${hasOTP ? 'bg-primary/10 border-primary' : 'bg-muted/50 border-border'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-xs font-semibold text-foreground">
                            {order.service_name || getServiceName(order.service)}
                          </span>
                          {order.server_name && (
                            <span className="ml-1.5 text-[10px] text-muted-foreground font-medium">{order.server_name}</span>
                          )}
                          {order.country && (
                            <span className="ml-1 text-[10px] text-muted-foreground">({getCountryName(order.country)})</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${hasOTP ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
                            {hasOTP ? '✓ Code Received' : 'Waiting...'}
                          </span>
                          {!hasOTP && (
                            <span className="text-[10px] text-muted-foreground font-mono">
                              {minutes}:{seconds.toString().padStart(2, '0')}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] text-muted-foreground">Phone:</span>
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-xs text-foreground">{formatPhoneDisplay(order.phone_number, order.server_name)}</span>
                          {order.phone_number && formatPhoneDisplay(order.phone_number, order.server_name) !== 'N/A' && (
                            <button
                              onClick={() => copyToClipboard(formatPhoneCopy(order.phone_number, order.server_name), 'Phone copied!')}
                              className="p-0.5 hover:bg-muted rounded"
                            >
                              <Copy className="w-3 h-3 text-muted-foreground" />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">OTP:</span>
                        {hasOTP ? (
                          <div className="flex items-center gap-1">
                            <span className="font-mono text-lg font-bold text-primary bg-card px-2 py-0.5 rounded">
                              {order.otp || order.otp_code}
                            </span>
                            <button
                              onClick={() => copyOTP(order.otp || order.otp_code)}
                              className="p-1 bg-primary/10 hover:bg-primary/10 rounded"
                            >
                              <Copy className="w-3 h-3 text-primary" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Waiting...
                          </span>
                        )}
                      </div>

                      {!hasOTP && canCancel && (
                        <button
                          onClick={() => handleCancelOrder(order.activation_id || order.id)}
                          className="w-full mt-2 py-1.5 bg-destructive/10 text-destructive hover:bg-destructive/10 rounded-md text-xs font-semibold"
                        >
                          Cancel Order
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-black">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Service</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Server</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Country</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Phone</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Code</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Status</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders
                    .filter((o) => {
                      if (o.status === 'active') return true;
                      if ((o.status === 'completed' || o.status === 'received') && (o.otp || o.otp_code)) {
                        const createdAt = new Date(o.created_at);
                        const now = new Date();
                        const elapsedMinutes = (now - createdAt) / (1000 * 60);
                        return elapsedMinutes < 10;
                      }
                      return false;
                    })
                    .map((order) => {
                      const createdAt = new Date(order.created_at);
                      const now = new Date();
                      const elapsedSeconds = Math.floor((now - createdAt) / 1000);
                      const timerDuration = getTimerDuration(order);
                      const remainingSeconds = Math.max(0, timerDuration - elapsedSeconds);
                      const minutes = Math.floor(remainingSeconds / 60);
                      const seconds = remainingSeconds % 60;
                      const hasOTP = order.otp || order.otp_code;
                      const canCancel = !hasOTP && remainingSeconds > 0;

                      return (
                        <tr key={order.id} className={`border-b ${hasOTP ? 'bg-primary/10' : 'hover:bg-muted/50'}`}>
                          <td className="py-3 px-3">
                            <span className="text-xs font-medium text-foreground">
                              {order.service_name || getServiceName(order.service)}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <span className="text-xs text-muted-foreground">
                              {order.server_name || '—'}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <span className="text-xs text-muted-foreground">
                              {order.country ? getCountryName(order.country) : '—'}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-xs text-foreground">{formatPhoneDisplay(order.phone_number, order.server_name)}</span>
                              {order.phone_number && formatPhoneDisplay(order.phone_number, order.server_name) !== 'N/A' && (
                                <button
                                  onClick={() => copyToClipboard(formatPhoneCopy(order.phone_number, order.server_name), 'Phone copied!')}
                                  className="p-0.5 hover:bg-muted rounded"
                                >
                                  <Copy className="w-3 h-3 text-muted-foreground" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            {hasOTP ? (
                              <div className="flex items-center gap-1">
                                <span className="font-mono text-lg font-bold text-primary bg-card px-2 py-0.5 rounded border border-primary">
                                  {order.otp || order.otp_code}
                                </span>
                                <button
                                  onClick={() => copyOTP(order.otp || order.otp_code)}
                                  className="p-1 bg-primary/10 hover:bg-primary/10 rounded"
                                >
                                  <Copy className="w-3 h-3 text-primary" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Waiting...
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex flex-col gap-0.5">
                              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full text-center w-fit ${hasOTP ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
                                {hasOTP ? '✓ Received' : 'Waiting'}
                              </span>
                              {!hasOTP && (
                                <span className="text-[10px] text-muted-foreground font-mono">
                                  {minutes}:{seconds.toString().padStart(2, '0')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            {!hasOTP && canCancel && (
                              <button
                                onClick={() => handleCancelOrder(order.activation_id || order.id)}
                                className="px-2 py-1 bg-destructive/10 text-destructive hover:bg-destructive/10 rounded text-[10px] font-semibold"
                              >
                                Cancel
                              </button>
                            )}
                            {!hasOTP && !canCancel && (
                              <span className="text-[10px] text-muted-foreground">Wait {Math.max(0, 180 - elapsedSeconds)}s</span>
                            )}
                            {hasOTP && (
                              <span className="text-xs text-primary font-semibold">✓ Complete</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-8 sm:py-12">
            <Phone className="w-10 h-10 sm:w-16 sm:h-16 mx-auto text-muted-foreground mb-3" />
            <p className="text-xs sm:text-sm text-muted-foreground">No active verifications</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Purchase a number to get started</p>
          </div>
        )}
      </div>

      {/* Provider/Operator Selection Bottom Sheet Modal */}
      {showProviderModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowProviderModal(false)}
          />
          
          {/* Modal Content - Full width, 70% height from bottom */}
          <div className="relative w-full bg-card rounded-t-3xl shadow-2xl h-[70vh] flex flex-col animate-slide-up">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-muted rounded-full" />
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-5 sm:px-6 pb-4 border-b border-border">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-foreground">Select operator</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  Choose the rate plan for this service
                </p>
              </div>
              <button 
                onClick={() => setShowProviderModal(false)}
                className="text-2xl text-muted-foreground hover:text-muted-foreground font-light"
              >
                ×
              </button>
            </div>
            
            {/* Provider List */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              {providersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-primary animate-spin" />
                  <span className="ml-2 text-muted-foreground">Loading operators...</span>
                </div>
              ) : serviceProviders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p className="text-sm sm:text-base">No operators available for this service</p>
                </div>
              ) : (
                // Sort by delivery rate (highest first) for 5sim, by price for others
                [...serviceProviders]
                  .sort((a, b) => {
                    // For 5sim, sort by delivery rate (highest first)
                    if (selectedProvider?.id === '5sim') {
                      return (b.delivery_rate || 0) - (a.delivery_rate || 0);
                    }
                    // For others, sort by price (lowest first)
                    return (a.price_ngn || 0) - (b.price_ngn || 0);
                  })
                  .map((provider, index) => {
                  const isSelected = selectedServiceProvider?.provider_id === provider.provider_id || 
                                     selectedServiceProvider?.operator === provider.operator;
                  const isOutOfStock = (provider.count || 0) === 0;
                  const hasDeliveryRate = provider.delivery_rate && provider.delivery_rate > 0;
                  
                  // Minimum price of ₦500
                  const displayPriceNgn = enforceMinPrice(provider.price_ngn || 0);
                  const displayPriceUsd = displayPriceNgn / 1500; // Convert back to USD for display
                  
                  return (
                    <button
                      key={provider.provider_id || provider.operator || index}
                      onClick={() => !isOutOfStock && handleProviderSelection({
                        ...provider,
                        price_ngn: displayPriceNgn,
                        price_usd: displayPriceUsd
                      })}
                      disabled={isOutOfStock}
                      className={`relative w-full p-4 sm:p-5 rounded-lg transition-all text-left ${
                        isSelected
                          ? 'bg-primary/10'
                          : 'bg-card hover:bg-muted/50'
                      } ${isOutOfStock ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {/* Recommended Badge - Only for first item with stock */}
                      {index === 0 && !isOutOfStock && (
                        <div className="absolute -top-2 left-4">
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-white text-[10px] sm:text-xs font-semibold rounded-full shadow-sm">
                            <Zap className="w-3 h-3" />
                            RECOMMENDED
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between gap-4 mt-2">
                        {/* Left Side - Operator Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base sm:text-lg font-bold text-foreground">
                            {provider.name || provider.operator || `Provider ${provider.provider_id}`}
                          </h4>
                          
                          {/* Success Rate & SMS Info */}
                          <div className="flex items-center gap-2 mt-1.5">
                            {hasDeliveryRate && (
                              <span className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground bg-card px-2 py-0.5 rounded">
                                <TrendingUp className="w-3 h-3" />
                                {provider.delivery_rate.toFixed(0)}%
                              </span>
                            )}
                            <span className="text-xs sm:text-sm text-muted-foreground bg-card px-2 py-0.5 rounded">
                              {'>'}1 SMS
                            </span>
                          </div>
                          
                          {/* Success Rate Note */}
                          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                            <span className="text-amber-600">⚡</span>
                            Success Rate: {hasDeliveryRate ? `${provider.delivery_rate.toFixed(0)}%` : 'Varies'}
                          </p>
                        </div>
                        
                        {/* Right Side - Price, Stock & Cart */}
                        <div className="text-right flex-shrink-0">
                          {/* Price and Cart on same line */}
                          <div className="flex items-center justify-end gap-2">
                            <div className="text-base sm:text-lg font-bold text-foreground">
                              ${displayPriceUsd.toFixed(4)} <span className="text-muted-foreground">(₦{displayPriceNgn.toFixed(0)})</span>
                            </div>
                            {/* Cart Icon */}
                            <div className={`inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${
                              isSelected ? 'bg-primary' : 'bg-primary'
                            }`}>
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            </div>
                          </div>
                          {/* Stock count */}
                          <p className={`text-xs sm:text-sm mt-1 ${isOutOfStock ? 'text-destructive' : 'text-primary'}`}>
                            {isOutOfStock ? 'Out of stock' : `${provider.count?.toLocaleString()} numbers`}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            
            {/* Footer Note */}
            <div className="px-4 sm:px-6 py-4 bg-muted/50 border-t border-border">
              <div className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                <span className="text-muted-foreground mt-0.5">ⓘ</span>
                <p>You will be issued one of the virtual numbers available in stock. Please note that the prices may vary</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
